import { datadogLogs } from '@datadog/browser-logs';

import { ENV_VARIABLES } from '@/constants';

import pkg from '../../../package.json';

import {
  DATADOG_REGION,
  DATADOG_SERVICE,
  DATADOG_SITE,
  DATADOG_TEAM,
  DEFAULT_SESSION_SAMPLE_RATE
} from './constants';
import hasDatadogCredentials from './hasDatadogCredentials';
import type { DatadogSampleRates } from './types';

/**
 * Initializes Datadog Logs. `@datadog/browser-logs` is a separate SDK that the
 * analytics provider does not wrap, so the app owns this init in both the
 * consent-aware and the legacy path.
 *
 * Returns `true` when initialization ran, `false` when it was skipped because
 * credentials are missing — the skip itself is reported by
 * `reportMissingDatadogCredentials`, so it stays silent here.
 */
const initDatadogLogs = ({
  sessionSampleRate = DEFAULT_SESSION_SAMPLE_RATE
}: DatadogSampleRates = {}): boolean => {
  if (!hasDatadogCredentials()) {
    return false;
  }

  datadogLogs.init({
    clientToken: ENV_VARIABLES.DATADOG_CLIENT_TOKEN,
    site: DATADOG_SITE,
    service: DATADOG_SERVICE,
    env: ENV_VARIABLES.ENVIRONMENT,
    version: pkg.version,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    sessionSampleRate
  });

  datadogLogs.setGlobalContext({
    team: DATADOG_TEAM,
    region: DATADOG_REGION,
    app_name: DATADOG_SERVICE
  });

  return true;
};

/**
 * Identifies Logs events with the signed-in user without attaching PII.
 * Datadog identity uses only the user id, matching the RUM privacy policy.
 */
export const setDatadogLogsUser = (id: string) => {
  datadogLogs.setUser({ id });
};

/** Clears the Logs identity when the signed-in user is removed. */
export const clearDatadogLogsUser = () => {
  datadogLogs.clearUser();
};

export default initDatadogLogs;
